import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { AppProvider } from "@/contexts/AppContext";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Onboarding from "@/pages/Onboarding";
import OnboardAccountant from "@/pages/OnboardAccountant";
import AuthCallback from "@/pages/AuthCallback";
import Layout from "@/components/Layout";
import Capture from "@/pages/Capture";
import Review from "@/pages/Review";
import Approval from "@/pages/Approval";
import History from "@/pages/History";
import EntryEdit from "@/pages/EntryEdit";
import JobsPage from "@/pages/Jobs";
import ExportPage from "@/pages/Export";
import Settings from "@/pages/Settings";
import TaxPage from "@/pages/Tax";
import PersonalTaxPage from "@/pages/PersonalTax";
import OHS from "@/pages/OHS";
import VehicleWfh from "@/pages/VehicleWfh";
import PhotoViewer from "@/pages/PhotoViewer";
import Privacy from "@/pages/Privacy";
import Terms from "@/pages/Terms";
import Landing from "@/pages/Landing";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import ErrorBoundary from "@/components/ErrorBoundary";
import "@/App.css";

/**
 * `EntryScreen` wraps every public-facing route (landing, login, register,
 * onboarding, privacy, terms, forgot/reset password) in an `<ErrorBoundary>`
 * so a runtime crash in one leaf can never white-screen the entry funnel.
 * If the boundary catches an error, it renders a small friendly fallback
 * with a Reload button — testers never see a blank page.
 */
function EntryScreen({ children }) {
  return <ErrorBoundary>{children}</ErrorBoundary>;
}

function Protected({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading || user === null) {
    return (
      <div className="min-h-screen flex items-center justify-center text-white/60">
        <div className="animate-pulse">Loading…</div>
      </div>
    );
  }
  if (user === false) {
    // Anon visitors get the public landing page at the root; every other
    // guarded path still bounces to /login so no protected content leaks
    // and deep-links (e.g. /history bookmarks) still funnel into sign-in.
    if (location.pathname === "/") return <EntryScreen><Landing /></EntryScreen>;
    return <Navigate to="/login" replace />;
  }
  if (!user.onboarded) return <Navigate to="/onboarding" replace />;
  return children;
}

/**
 * Detect an OAuth callback in the URL hash synchronously during render so it
 * runs BEFORE any ProtectedRoute / /auth/me check. Prevents race conditions.
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH.
 */
function AppRoutes() {
  const location = useLocation();
  if (location.hash && location.hash.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/login" element={<EntryScreen><Login /></EntryScreen>} />
      <Route path="/register" element={<EntryScreen><Register /></EntryScreen>} />
      <Route path="/onboarding" element={<EntryScreen><Onboarding /></EntryScreen>} />
      <Route path="/onboard-accountant" element={<EntryScreen><OnboardAccountant /></EntryScreen>} />
      <Route path="/privacy" element={<EntryScreen><Privacy /></EntryScreen>} />
      <Route path="/terms" element={<EntryScreen><Terms /></EntryScreen>} />
      <Route path="/forgot-password" element={<EntryScreen><ForgotPassword /></EntryScreen>} />
      <Route path="/reset-password" element={<EntryScreen><ResetPassword /></EntryScreen>} />
      {/* Standalone photo viewer — PUBLIC route rendered OUTSIDE Layout so
          it opens clean in a new tab (no bottom nav / top bar) AND works
          when the iOS Home-Screen PWA opens `target='_blank'` in external
          Safari, where the PWA's cookie jar isn't available. Auth is
          instead carried by a short-lived `?token=` in the /api/files
          fetch. See `useFileToken` + backend `create_download_token`. */}
      <Route
        path="/photo"
        element={<EntryScreen><PhotoViewer /></EntryScreen>}
      />
      <Route
        path="/"
        element={<Protected><Layout /></Protected>}
      >
        <Route index element={<Capture />} />
        <Route path="review" element={<Review />} />
        <Route path="approval" element={<Approval />} />
        <Route path="history" element={<History />} />
        <Route path="entries/:id/edit" element={<EntryEdit />} />
        <Route path="jobs" element={<JobsPage />} />
        <Route path="export" element={<ExportPage />} />
        <Route path="settings" element={<Settings />} />
        <Route path="tax" element={<TaxPage />} />
        <Route path="personal-tax" element={<PersonalTaxPage />} />
        <Route path="ohs" element={<OHS />} />
        <Route path="vehicle-wfh" element={<VehicleWfh />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="min-h-screen bi-grain bi-ambient" data-testid="app-root">
      <BrowserRouter>
        <AuthProvider>
          <AppProvider>
            <AppRoutes />
            <Toaster theme="dark" position="top-center" richColors />
          </AppProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
